/**
 * Reading a product for its page and for the lists that link to it
 * (z8nrz7g57f, z8nrz7g57g).
 *
 * The product requirements mock assumed fields the system does not hold — a
 * release flag on the bag, an expiry, a "viable CD34+" dose, a donor record.
 * These rules say what each figure on the page really is, from what IS
 * recorded, so the Products list, the product page and the patient page agree.
 * Pure, so they are tested without a DOM.
 */
import type { AuditTrailRow } from '@/types/audit'
import type { CreatedProduct, ProductRecord } from '@/types/products'
import { formatLimsValue, limsRowsOf, type LimsResultKey } from './limsResults'

/**
 * The product's name from its ISBT description: the part before the first
 * `|` ("HPC, APHERESIS" out of "HPC, APHERESIS|Citrate/XX/rt|Mobilized").
 * The rest are the attributes the page lists one by one. Null for no description.
 */
export function catalogName(description: string | null | undefined): string | null {
  const name = description?.split('|')[0]?.trim()
  return name ? name : null
}

/** "Div A0 of 2" — the division, and how many products the split made, when known. */
export function divisionLabel(division: string | null | undefined, ofCount?: number): string | null {
  if (!division) return null
  return ofCount && ofCount > 1 ? `Div ${division} of ${ofCount}` : `Div ${division}`
}

/** The division suffix of a product listed without its catalog entry: the code past its 5-character base. */
export function divisionOfCode(productDescriptionCode: string): string | null {
  return /^[A-Z][0-9A-Z]{4}[A-Za-z0-9]+$/i.test(productDescriptionCode) ? productDescriptionCode.slice(5) : null
}

export type Tone = 'green' | 'amber' | 'blue' | 'gray'

/** Where the bag is now, as a pill. A product has no inventory status field; this is it. */
export function inventoryState(p: Pick<CreatedProduct, 'supersededById' | 'locationPartner' | 'storageNodeId' | 'volumeMl'>): {
  label: string
  tone: Tone
} {
  if (p.supersededById) return { label: 'Superseded', tone: 'gray' }
  if (p.locationPartner) return { label: `Shipped to ${p.locationPartner.name}`, tone: 'amber' }
  if (p.storageNodeId) return { label: 'In storage', tone: 'green' }
  // A split leaves its parent at 0 mL: the bag became its divisions.
  if (p.volumeMl === 0) return { label: 'Divided', tone: 'gray' }
  return { label: 'Not placed', tone: 'gray' }
}

/**
 * Release, read from the run's product release review form — there is no
 * release flag on a product. Null when the run has no such form, so the page
 * does not claim "not released" for a run that never reviews release.
 */
export function releaseState(release: ProductRecord['release'] | undefined): { label: string; tone: Tone } | null {
  if (release === 'released') return { label: 'Released', tone: 'green' }
  if (release === 'awaiting') return { label: 'Not yet released', tone: 'amber' }
  return null
}

/** One "dose per kg of recipient" card. `value` is null when the result is not recorded. */
export interface DoseCard {
  label: string
  value: string | null
  unit: string
}

/**
 * The three per-kg doses the server recorded for this product. CD34 and CD3
 * are recorded single- and dual-platform; single platform is shown, dual
 * platform when it is the only one. The figures are the server's
 * (`lims-calculations.ts`); nothing is computed here.
 */
export function doseCards(limsValues: unknown): DoseCard[] {
  // One decimal, as a dose is read ("4.0", "2.1"); a very small figure keeps its significant digits.
  const first = (keys: LimsResultKey[]) => {
    const row = limsRowsOf(limsValues, keys)[0]
    if (!row) return null
    return Math.abs(row.value) >= 0.1 ? row.value.toFixed(1) : formatLimsValue(row.value)
  }
  return [
    { label: 'TNC', value: first(['tnc_per_kg']), unit: '×10⁸/kg' },
    { label: 'CD34+', value: first(['cd34_per_kg_sp', 'cd34_per_kg_dp']), unit: '×10⁶/kg' },
    { label: 'CD3+', value: first(['cd3_per_kg_sp', 'cd3_per_kg_dp']), unit: '×10⁸/kg' },
  ]
}

/** "TNC 4.0 ×10⁸ · CD34+ 2.1 ×10⁶" for a table cell, or null with no dose recorded. */
export function doseSummary(limsValues: unknown): string | null {
  const parts = doseCards(limsValues)
    .filter((c) => c.value != null && c.label !== 'CD3+')
    .map((c) => `${c.label} ${c.value} ${c.unit.replace('/kg', '')}`)
  return parts.length ? parts.join(' · ') : null
}

/** The product events that are its chain of custody, and how each reads there. */
const CUSTODY_TITLES: Record<string, string> = {
  product_created: 'Created',
  product_received: 'Received',
  split_performed: 'Divided',
  split_child_created: 'Divided from parent',
  pool_performed: 'Pooled',
  label_printed: 'Label printed',
  label_verified: 'Label verified',
  product_stored: 'Placed in storage',
  product_vacated: 'Removed from storage',
  product_shipped: 'Shipped',
  product_returned: 'Returned',
}

export interface CustodyEvent {
  id: string
  title: string
  at: string
  by: string | null
  detail: string | null
}

/**
 * The chain of custody: who had the bag and where, oldest first, out of the
 * product's audit trail. Edits that are not custody (volume, results, code)
 * stay in the trail and are left out here.
 */
export function custodyEvents(rows: readonly AuditTrailRow[], parentRows: readonly AuditTrailRow[] = []): CustodyEvent[] {
  // A division's custody starts with its parent's: collected and received as the
  // whole bag, until the split that made it.
  const fromParent = parentRows.filter((r) => PARENT_CUSTODY.has(r.eventType))
  return [...fromParent, ...rows]
    .filter((r) => r.productId && CUSTODY_TITLES[r.eventType])
    .map((r) => ({
      id: r.id,
      title: r.eventType === 'split_performed' && fromParent.includes(r) ? 'Divided' : CUSTODY_TITLES[r.eventType],
      at: r.changedAt,
      by: actorName(r),
      detail: r.description ?? null,
    }))
    // The parent's split and this division's creation are one moment: keep the split.
    .filter((e, _i, all) => !(e.title === 'Divided from parent' && all.some((x) => x.title === 'Divided' && x.at === e.at)))
    .sort((a, b) => Date.parse(a.at) - Date.parse(b.at))
}

/** The parent's events that are part of a division's custody. */
const PARENT_CUSTODY = new Set(['product_created', 'product_received', 'split_performed'])

/** Who acted, by name; a bare id says nothing to a reader, so it is left out. */
function actorName(r: AuditTrailRow): string | null {
  const name = r.changedByActor?.name || r.changedByActor?.email
  if (name) return name
  return /^[0-9a-f]{8}-[0-9a-f]{4}-/i.test(r.changedBy) ? null : r.changedBy
}

/** "7 days" since it was placed, or null. Whole days, so it does not tick on screen. */
export function timeInStorage(storedAt: string | null | undefined, now: number = Date.now()): string | null {
  if (!storedAt) return null
  const days = Math.floor((now - Date.parse(storedAt)) / 86_400_000)
  if (!Number.isFinite(days) || days < 0) return null
  if (days === 0) return 'Less than a day'
  return days === 1 ? '1 day' : `${days} days`
}
